import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './lib/auth';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Spinner } from './components/Spinner';
import { Seo } from './components/Seo';

// Route-level code splitting: every page is dynamically imported so the
// initial JS bundle only contains the shell + auth/routing primitives. The
// Stripe-heavy Subscription page and the entire admin section now ship as
// their own chunks, fetched on demand.
const MarketingPage = lazy(() => import('./pages/Marketing'));
const LoginPage = lazy(() => import('./pages/Login'));
const RegisterPage = lazy(() => import('./pages/Register'));
const OperatorsPage = lazy(() => import('./pages/Operators'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPassword'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPassword'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmail'));
const VerifyPhonePage = lazy(() => import('./pages/VerifyPhone'));
const VerifyEmailPendingPage = lazy(() => import('./pages/VerifyEmailPending'));
const DownloadPage = lazy(() => import('./pages/Download'));
const HelpPage = lazy(() => import('./pages/Help'));
const SupportPage = lazy(() => import('./pages/Support'));
const PrivacyPage = lazy(() => import('./pages/Privacy'));
const TermsPage = lazy(() => import('./pages/Terms'));
const DeleteAccountPage = lazy(() => import('./pages/DeleteAccount'));
const StripeOnboardingPage = lazy(() => import('./pages/onboarding/Stripe'));
const DashboardIndex = lazy(() => import('./pages/dashboard/Index'));
const SubscriptionPage = lazy(() => import('./pages/dashboard/Subscription'));
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const AdminStats = lazy(() => import('./pages/admin/Stats'));
const AdminOperators = lazy(() => import('./pages/admin/Operators'));
const AdminOperatorDetail = lazy(() => import('./pages/admin/OperatorDetail'));
const AdminBookings = lazy(() => import('./pages/admin/Bookings'));
const AdminBookingDetail = lazy(() => import('./pages/admin/BookingDetail'));
const AdminUsers = lazy(() => import('./pages/admin/Users'));
const AdminUserDetail = lazy(() => import('./pages/admin/UserDetail'));
const AdminEmailTemplates = lazy(() => import('./pages/admin/EmailTemplates'));
const AdminEmailTemplateDetail = lazy(() => import('./pages/admin/EmailTemplateDetail'));

function RoleRedirect() {
  const { user, isLoading } = useAuth();
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }
  // Logged-out visitors land on the marketing page instead of being bounced
  // straight to /login — gives us somewhere to actually sell the product.
  if (!user) return <MarketingPage />;
  if (user.role === 'admin') return <Navigate to="/admin" replace />;
  if (user.role === 'operator') return <Navigate to="/dashboard" replace />;
  // Customers have no web area yet; nudge them back to the marketing page
  // (where the App Store / Play Store CTAs live).
  return <Navigate to="/login" replace />;
}

function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-navy">
      <Spinner size="lg" />
    </div>
  );
}

export default function App() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Seo />
      <Routes>
        <Route path="/" element={<RoleRedirect />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/operators" element={<OperatorsPage />} />
        <Route path="/forgot" element={<ForgotPasswordPage />} />
        <Route path="/reset/:token" element={<ResetPasswordPage />} />
        <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
        <Route
          path="/verify-phone"
          element={
            <ProtectedRoute role="operator">
              <VerifyPhonePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/verify-email"
          element={
            <ProtectedRoute role="operator">
              <VerifyEmailPendingPage />
            </ProtectedRoute>
          }
        />
        <Route path="/download" element={<DownloadPage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/delete-account" element={<DeleteAccountPage />} />

        <Route
          path="/onboarding"
          element={
            <ProtectedRoute role="operator">
              <StripeOnboardingPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard"
          element={
            <ProtectedRoute role="operator">
              <DashboardIndex />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/subscription"
          element={
            <ProtectedRoute role="operator">
              <SubscriptionPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin"
          element={
            <ProtectedRoute role="admin">
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="stats" replace />} />
          <Route path="stats" element={<AdminStats />} />
          <Route path="operators" element={<AdminOperators />} />
          <Route path="operators/:id" element={<AdminOperatorDetail />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="users/:id" element={<AdminUserDetail />} />
          <Route path="bookings" element={<AdminBookings />} />
          <Route path="bookings/:id" element={<AdminBookingDetail />} />
          <Route path="email-templates" element={<AdminEmailTemplates />} />
          <Route path="email-templates/:key" element={<AdminEmailTemplateDetail />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
